import { Link } from "@tanstack/react-router";
import { Facehash } from "facehash";
import {
	Bot,
	ChevronDown,
	LogOut,
	Megaphone,
	MessageSquare,
	Settings,
	User,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { capitalizeName } from "../../lib/format";

interface UserMenuProps {
	user: {
		id: string;
		name?: string | null;
		email?: string;
		image?: string | null;
		role?: string | null;
	};
	isImpersonating: boolean;
	isSigningOut: boolean;
	onSignOut: () => void;
}

export function UserMenu({
	user,
	isImpersonating,
	isSigningOut,
	onSignOut,
}: UserMenuProps) {
	const userAvatarAlt = capitalizeName(user.name) || user.email || "User";

	const menuItemClass =
		"flex cursor-pointer items-center gap-3 px-3 py-2 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:bg-brand-soft hover:text-brand";

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button
					type="button"
					className="relative flex items-center gap-2 border border-border bg-background pl-1 pr-2 py-1 transition-[border-color,transform] hover:border-brand/40 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background before:absolute before:-inset-y-[7px] before:inset-x-0 before:content-[''] group cursor-pointer"
				>
					<div className="size-7 border border-border bg-primary flex items-center justify-center overflow-hidden">
						{user.image ? (
							<img
								src={user.image}
								alt={userAvatarAlt}
								className="size-full object-cover"
								loading="lazy"
								referrerPolicy="no-referrer"
							/>
						) : (
							<Facehash name={user.email || user.id} size={26} />
						)}
					</div>
					<span className="hidden lg:block font-mono text-sm uppercase tracking-[0.12em] text-foreground max-w-[120px] truncate">
						{capitalizeName(user.name) || "User"}
					</span>
					<ChevronDown className="size-3 text-muted-foreground group-hover:text-brand transition-colors" />
				</button>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				align="end"
				sideOffset={6}
				className="w-72 p-0 bg-card border-border rounded-none"
			>
				<div className="p-3 border-b border-border">
					<div className="flex items-center gap-3">
						<div className="size-10 border border-border bg-primary flex items-center justify-center overflow-hidden shrink-0">
							{user.image ? (
								<img
									src={user.image}
									alt={userAvatarAlt}
									className="size-full object-cover"
									loading="lazy"
									referrerPolicy="no-referrer"
								/>
							) : (
								<Facehash name={user.email || user.id} size={36} />
							)}
						</div>
						<div className="flex flex-col min-w-0 flex-1">
							<span className="font-mono text-sm font-normal uppercase tracking-[0.08em] text-foreground truncate">
								{capitalizeName(user.name) || "User"}
							</span>
							<span className="truncate font-mono text-sm text-muted-foreground">
								{user.email}
							</span>
						</div>
					</div>
				</div>

				<div className="py-1">
					<DropdownMenuItem asChild>
						<Link to="/dashboard/agents" className={menuItemClass}>
							<Bot className="size-3.5" />
							My Agents
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to="/dashboard/founder" className={menuItemClass}>
							<User className="size-3.5" />
							Founder
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link to="/settings" className={menuItemClass}>
							<Settings className="size-3.5" />
							Settings
						</Link>
					</DropdownMenuItem>
				</div>

				{user.role === "admin" && !isImpersonating && (
					<>
						<div className="border-t border-border px-3 pt-2 pb-1">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Admin
							</span>
						</div>
						<div className="pb-1">
							{[
								{ to: "/admin/users", icon: User, label: "Users" },
								{
									to: "/admin/feedback",
									icon: MessageSquare,
									label: "Feedback",
								},
								{ to: "/admin/agents", icon: Bot, label: "Agents" },
								{ to: "/admin/ads", icon: Megaphone, label: "Ads" },
							].map(({ to, icon: Icon, label }) => (
								<DropdownMenuItem asChild key={to}>
									<Link to={to} className={menuItemClass}>
										<Icon className="size-3.5" />
										{label}
									</Link>
								</DropdownMenuItem>
							))}
						</div>
					</>
				)}

				<div className="border-t border-border">
					<DropdownMenuItem
						disabled={isSigningOut}
						onClick={onSignOut}
						className="flex cursor-pointer items-center gap-3 px-3 py-2 font-mono text-sm uppercase tracking-[0.12em] text-[color:var(--ticker-down)] transition-colors hover:bg-red-500/10"
					>
						<LogOut className="size-3.5" />
						Sign out
					</DropdownMenuItem>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
